"use client";

import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { CheckCircle2, Mail, Phone } from "lucide-react";
import { CONTACT, FORM_ENDPOINT } from "@/config/site";
import { PROJECT_TYPES } from "@/config/constants";
import { Button } from "@/components/ui/Button";
import { Field, inputClass } from "@/components/ui/Field";
import { Checkbox } from "@/components/ui/Checkbox";
import {
  EMPTY_CONTACT,
  validateContact,
  type ContactErrors,
  type ContactValues,
} from "@/components/forms/validation";

type Status = "idle" | "invalid" | "submitting" | "demo" | "success" | "error";

export function ContactForm() {
  const [values, setValues] = useState<ContactValues>(EMPTY_CONTACT);
  const [errors, setErrors] = useState<ContactErrors>({});
  const [status, setStatus] = useState<Status>("idle");
  const formRef = useRef<HTMLFormElement>(null);

  const update = <K extends keyof ContactValues>(
    key: K,
    value: ContactValues[K],
  ) => {
    setValues((previous) => ({ ...previous, [key]: value }));
    if (errors[key]) {
      setErrors((previous) => ({ ...previous, [key]: undefined }));
    }
  };

  // Předvyplnění typu zakázky z karet "Poptat vinyl / laminát / dřevo".
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const link = target?.closest<HTMLElement>("[data-floor-type]");
      const value = link?.dataset.floorType;
      if (!value) return;
      setValues((previous) => ({ ...previous, projectType: value }));
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const nextErrors = validateContact(values);
    setErrors(nextErrors);

    const errorKeys = Object.keys(nextErrors) as Array<keyof ContactValues>;
    if (errorKeys.length > 0) {
      setStatus("invalid");
      const first = errorKeys[0];
      formRef.current
        ?.querySelector<HTMLElement>(`[name="${first}"]`)
        ?.focus();
      return;
    }

    if (!FORM_ENDPOINT) {
      setStatus("demo");
      return;
    }

    setStatus("submitting");
    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        throw new Error("Request failed");
      }

      setValues(EMPTY_CONTACT);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  };

  const errorMessages = Object.values(errors).filter(
    (message): message is string => Boolean(message),
  );

  return (
    <form
      ref={formRef}
      onSubmit={handleSubmit}
      noValidate
      className="flex flex-col gap-6"
    >
      {status === "invalid" && errorMessages.length > 0 ? (
        <div
          role="alert"
          className="rounded-xs border border-clay bg-clay-tint px-4 py-3"
        >
          <p className="font-sans text-sm font-medium text-ink">
            Formulář obsahuje chyby:
          </p>
          <ul className="mt-2 list-disc pl-5 font-sans text-sm text-clay-dark">
            {errorMessages.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        <Field id="contact-name" label="Jméno a příjmení" required error={errors.name}>
          <input
            id="contact-name"
            name="name"
            type="text"
            autoComplete="name"
            value={values.name}
            onChange={(event) => update("name", event.target.value)}
            aria-required="true"
            aria-invalid={errors.name ? true : undefined}
            aria-describedby={errors.name ? "contact-name-error" : undefined}
            className={inputClass}
          />
        </Field>

        <Field id="contact-phone" label="Telefon" required error={errors.phone}>
          <input
            id="contact-phone"
            name="phone"
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={values.phone}
            onChange={(event) => update("phone", event.target.value)}
            aria-required="true"
            aria-invalid={errors.phone ? true : undefined}
            aria-describedby={errors.phone ? "contact-phone-error" : undefined}
            className={inputClass}
          />
        </Field>

        <Field id="contact-email" label="E-mail" error={errors.email}>
          <input
            id="contact-email"
            name="email"
            type="email"
            autoComplete="email"
            value={values.email}
            onChange={(event) => update("email", event.target.value)}
            aria-invalid={errors.email ? true : undefined}
            aria-describedby={errors.email ? "contact-email-error" : undefined}
            className={inputClass}
          />
        </Field>

        <Field id="contact-type" label="Typ zakázky">
          <select
            id="contact-type"
            name="projectType"
            value={values.projectType}
            onChange={(event) => update("projectType", event.target.value)}
            className={inputClass}
          >
            <option value="">Vyberte…</option>
            {PROJECT_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </Field>

        <Field id="contact-locality" label="Lokalita" className="sm:col-span-2">
          <input
            id="contact-locality"
            name="locality"
            type="text"
            autoComplete="address-level2"
            value={values.locality}
            onChange={(event) => update("locality", event.target.value)}
            className={inputClass}
          />
        </Field>

        <Field id="contact-message" label="Zpráva" className="sm:col-span-2">
          <textarea
            id="contact-message"
            name="message"
            rows={4}
            value={values.message}
            onChange={(event) => update("message", event.target.value)}
            className={inputClass}
          />
        </Field>
      </div>

      <Checkbox
        id="contact-consent"
        name="consent"
        checked={values.consent}
        onChange={(checked) => update("consent", checked)}
        error={errors.consent}
      />

      <div>
        <Button type="submit" disabled={status === "submitting"}>
          {status === "submitting" ? "Odesílám…" : "Odeslat poptávku"}
        </Button>
      </div>

      {status === "demo" ? (
        <div className="rounded-xs border border-clay bg-clay-tint px-5 py-5">
          <p className="font-sans text-sm text-ink">
            Toto je ukázkový formulář. V této ukázce se data neposílají —
            napište prosím na{" "}
            <a href={CONTACT.emailHref} className="link-hairline font-medium">
              {CONTACT.email}
            </a>{" "}
            nebo zavolejte{" "}
            <a href={CONTACT.phoneHref} className="link-hairline font-medium">
              {CONTACT.phone}
            </a>
            .
          </p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <a
              href={CONTACT.emailHref}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xs bg-clay px-5 font-sans text-sm font-medium text-paper hover:bg-clay-dark"
            >
              <Mail aria-hidden="true" size={18} strokeWidth={1.5} />
              Napsat e-mail
            </a>
            <a
              href={CONTACT.phoneHref}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xs border border-clay px-5 font-sans text-sm font-medium text-clay-dark hover:bg-white"
            >
              <Phone aria-hidden="true" size={18} strokeWidth={1.5} />
              {CONTACT.phone}
            </a>
          </div>
        </div>
      ) : null}

      {status === "success" ? (
        <div className="flex items-start gap-3 rounded-xs border border-clay bg-clay-tint px-5 py-4">
          <CheckCircle2
            aria-hidden="true"
            size={20}
            strokeWidth={1.5}
            className="mt-0.5 text-clay-dark"
          />
          <p className="font-sans text-sm text-ink">
            Poptávka byla odeslána. Ozvu se s dalším postupem.
          </p>
        </div>
      ) : null}

      {status === "error" ? (
        <div
          role="alert"
          className="rounded-xs border border-clay px-5 py-4"
        >
          <p className="font-sans text-sm text-ink">
            Odeslání se nepodařilo. Zkuste to prosím znovu, nebo napište na{" "}
            <a href={CONTACT.emailHref} className="link-hairline font-medium">
              {CONTACT.email}
            </a>
            .
          </p>
        </div>
      ) : null}
    </form>
  );
}
